import type {
  ModuleAvailabilityReason,
  ResolvedModuleConfiguration,
} from '@features/settings/models'

/** `unknown_module` when the tenant's catalog lacks the module (e.g. an unmigrated database). */
export type ModuleAccessReason = ModuleAvailabilityReason | 'unknown_module' | 'loading'

export interface ModuleAccess {
  available: boolean
  reason: ModuleAccessReason
}

type Modules = Record<string, ResolvedModuleConfiguration> | null

/** The backend's resolution for one module (see docs/specs/MODULE_ACCESS.md). */
export function getModuleAccess(modules: Modules, slug: string): ModuleAccess {
  if (modules === null) return { available: false, reason: 'loading' }
  const module = modules[slug]
  if (!module) return { available: false, reason: 'unknown_module' }
  const available = module.is_enabled
  const reason = module.availability_reason ?? (available ? 'available' : 'not_licensed')
  return { available, reason }
}

/**
 * The permission module a person needs to open a catalog module, or null when
 * anyone in the organization may (the AI Assistant), or the module is unknown.
 * Data's is granted by read on any table; the rest are granted on a permission
 * group's Modules tab.
 */
export function getModulePermissionKey(modules: Modules, slug: string | undefined): string | null {
  if (modules === null || slug === undefined) return null
  const module = modules[slug]
  if (!module || module.permission_mode === undefined || module.permission_mode === 'none') {
    return null
  }
  return module.permission_module_key ?? null
}

/** The permission modules granted directly, on a permission group's Modules tab. */
export function getPrimaryPermissionModuleKeys(modules: Modules): ReadonlySet<string> {
  return new Set(
    Object.values(modules ?? {}).flatMap((module) =>
      module.permission_mode === 'primary' && module.permission_module_key
        ? [module.permission_module_key]
        : []
    )
  )
}

/**
 * Where a module lists on the Modules page: required first, then those the
 * organization can switch, then coming soon.
 */
function moduleListGroup(module: ResolvedModuleConfiguration | null): number {
  if (module?.availability_reason === 'coming_soon') return 2
  if (module?.is_required) return 0
  return 1
}

/**
 * License Settings in Modules page order: required, switchable, then coming
 * soon, each A–Z by label. A setting no module claims keeps its place after them.
 */
export function orderModuleLicenseSettings<
  T extends { setting_key: string; setting_label: string },
>(settings: readonly T[], modules: Modules): T[] {
  const withModule = settings.map((setting, index) => ({
    setting,
    index,
    module: getModuleForLicenseSetting(modules, setting.setting_key),
  }))
  return withModule
    .sort((a, b) => {
      if (!a.module || !b.module)
        return (a.module ? 0 : 1) - (b.module ? 0 : 1) || a.index - b.index
      return (
        moduleListGroup(a.module) - moduleListGroup(b.module) ||
        a.setting.setting_label.localeCompare(b.setting.setting_label)
      )
    })
    .map(({ setting }) => setting)
}

/** The module a license Setting switches on, so its toggle can show the module's state. */
export function getModuleForLicenseSetting(
  modules: Modules,
  settingKey: string
): ResolvedModuleConfiguration | null {
  if (modules === null) return null
  return Object.values(modules).find((module) => module.license_setting_key === settingKey) ?? null
}
